import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Dashboard from '../../../assets/user/Dashboard';
import Wallet from '../../../assets/Wallet';
import Transfer from '../../../assets/user/Transfer';
import { HomeOutlined, MoreOutlined, TransactionOutlined } from '@ant-design/icons';
import { LuBadgePercent, LuEllipsisVertical, LuHouse, LuUsers } from 'react-icons/lu';
import { isIbRestrictedHierarchy } from '../../../utils/utils.js';

const IB_BOTTOM_BAR_HIDDEN_WHEN_RESTRICTED = new Set(['Commission', 'More']);

export default function MobileBottomBar({ ib }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { userDetails } = useSelector((state) => state.user);

    const getFirstPath = (path) => {
        const parts = path.split('/');
        if (ib) {
            return parts.length > 1 ? '/' + parts[1] + '/' + parts[2] : path;
        } else {
            return parts.length > 1 ? '/' + parts[1] : path;
        }
    };

    const isActivePath = (activeUrls) => {
        return activeUrls.some(url => url === getFirstPath(pathname));
    };

    const userNavItems = [
        {
            title: "Home",
            icon: <HomeOutlined />,
            url: "/dashboard",
            activeUrl: ["/dashboard", "/account"]
        },
        {
            title: "My Wallet",
            icon: <Wallet />,
            url: "/wallet",
            activeUrl: ["/wallet", "/withdraw", "/deposit_usdt"],
            activeColor: "#193CB8"
        },
        {
            title: "Transfer",
            icon: <Transfer />,
            url: "/transfer",
            activeUrl: ["/transfer"],
            activeColor: "#193CB8"
        },
        {
            title: "Transaction",
            icon: <TransactionOutlined style={{ fontSize: '20px' }} />,
            url: "/my_transaction",
            activeUrl: ["/my_transaction", "/transaction"],
            activeColor: "#193CB8"
        },
        {
            title: "More",
            icon: <MoreOutlined style={{ fontSize: '20px' }} />,
            url: "/settings",
            activeUrl: ["/settings", "/accounts", "/trades", "/support", "/profile", "/change_password", "/leaderboard", "/tutorials", "/faq", "/copy_trading", "/copy_trading_ratings", "/paam", "/paam_ratings", "/reports", "/download", "/kyc"],
            activeColor: "#193CB8"
        },
    ];

    const ibNavItems = [
        {
            title: "Home",
            icon: <LuHouse size={20} />,
            url: "/ib/dashboard",
            activeUrl: ["/ib/dashboard"],
            activeColor: "#193CB8"
        },
        {
            title: "IB Wallet",
            icon: <Wallet />,
            url: "/ib/withdraw",
            activeUrl: ["/ib/withdraw"],
            activeColor: "#193CB8"
        },
        {
            title: "My Team",
            icon: <LuUsers size={20} />,
            url: "/ib/my_trader_team",
            activeUrl: ["/ib/my_trader_team", "/ib/tree"],
            activeColor: "#193CB8"
        },
        {
            title: "Commission",
            icon: <LuBadgePercent size={20} />,
            url: "/ib/commission",
            activeUrl: ["/ib/commission", "/ib/commission_distribute"],
            activeColor: "#193CB8"
        },
        {
            title: "More",
            icon: <LuEllipsisVertical size={20} />,
            url: "/ib/settings",
            activeUrl: ["/ib/settings", "/ib/profile", "/ib/change_password"],
            activeColor: "#193CB8"
        },
    ];

    const ibRestricted = ib && isIbRestrictedHierarchy(userDetails);
    const bottomNavItems = ib
        ? ibNavItems.filter((item) => !ibRestricted || !IB_BOTTOM_BAR_HIDDEN_WHEN_RESTRICTED.has(item.title))
        : userNavItems;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100 shadow-lg lg:hidden">
            <div className="flex justify-around items-center max-w-full mx-auto px-2 py-2">
                {bottomNavItems.map((item, index) => {
                    const isActive = isActivePath(item.activeUrl);
                    const iconWithProps = React.isValidElement(item.icon) && item.icon.type !== 'svg'
                        ? React.cloneElement(item.icon, { active: isActive, activeColor: item.activeColor })
                        : item.icon;

                    return (
                        <div
                            key={index}
                            className={`flex flex-col items-center justify-center py-2 px-1 cursor-pointer transition-all duration-200 rounded-xl min-w-[60px] flex-1 max-w-[80px] hover:bg-blue-50 text-gray-600`}
                            style={isActive ? { color: item.activeColor } : {}}
                            onClick={() => navigate(item.url)}
                        >
                            <div className="mb-1 flex items-center justify-center w-8 h-8">
                                <div className={`transition-colors duration-200 ${isActive ? 'primary' : 'text-gray-500'}`}
                                    style={isActive ? { color: item.activeColor } : {}}>
                                    {iconWithProps}
                                </div>
                            </div>
                            <span className={`text-[11px] font-medium text-center leading-tight transition-colors duration-200 whitespace-nowrap overflow-hidden text-ellipsis max-w-full ${isActive ? 'primary' : 'text-gray-600'}`}>
                                {item.title}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}